import type { TSchema } from "@sinclair/typebox"
import { Value } from "@sinclair/typebox/value"
import { useCallback, useEffect, useMemo, useState } from "react"

const STORED_STATE_SYNC_EVENT = "craft-force:stored-state-sync"

type StoredStateSyncDetail = {
  key: string
  raw: string
}

/**
 * Versioned persisted state options.
 */
export type StoredStateOptions<T> = {
  defaultValue: T
  version: number
  migrate?: (value: unknown, fromVersion: number, toVersion: number) => T
}

type StoredPayload<T> = {
  v: number
  value: T
}

type BrowserStorage = Pick<Storage, "getItem" | "setItem">

/**
 * Reads a versioned payload from storage and validates it against the provided schema.
 */
export function readStoredStateValue<T>(
  raw: string | null,
  schema: TSchema,
  options: StoredStateOptions<T>,
): T {
  if (!raw) {
    return options.defaultValue
  }

  try {
    const parsed = JSON.parse(raw) as StoredPayload<unknown>
    if (!parsed || typeof parsed !== "object" || typeof parsed.v !== "number") {
      return options.defaultValue
    }

    if (parsed.v !== options.version) {
      if (!options.migrate) {
        return options.defaultValue
      }

      return options.migrate(parsed.value, parsed.v, options.version)
    }

    const converted = Value.Convert(schema, parsed.value)
    return Value.Check(schema, converted)
      ? (converted as T)
      : options.defaultValue
  } catch {
    return options.defaultValue
  }
}

/**
 * Serializes a versioned payload for storage.
 */
export function writeStoredStateValue<T>(value: T, version: number): string {
  return JSON.stringify({
    v: version,
    value,
  } satisfies StoredPayload<T>)
}

/**
 * Persists state to local storage and rehydrates it on first render.
 */
export function useStoredState<T>(
  key: string,
  schema: TSchema,
  options: StoredStateOptions<T>,
): readonly [T, (next: T | ((previous: T) => T)) => void] {
  const storage = useMemo<BrowserStorage | null>(() => {
    if (typeof window === "undefined") {
      return null
    }

    const localStorageCandidate = window.localStorage as Partial<Storage> | null
    if (
      !localStorageCandidate ||
      typeof localStorageCandidate.getItem !== "function" ||
      typeof localStorageCandidate.setItem !== "function"
    ) {
      return null
    }

    return {
      getItem: localStorageCandidate.getItem.bind(localStorageCandidate),
      setItem: localStorageCandidate.setItem.bind(localStorageCandidate),
    }
  }, [])

  const [value, setValue] = useState<T>(() =>
    readStoredStateValue(storage?.getItem(key) ?? null, schema, options),
  )

  useEffect(() => {
    if (!storage) {
      return
    }

    storage.setItem(key, writeStoredStateValue(value, options.version))
  }, [key, options.version, storage, value])

  useEffect(() => {
    if (!storage || typeof window === "undefined") {
      return
    }

    const handleCustomSync = (event: Event) => {
      const customEvent = event as CustomEvent<StoredStateSyncDetail>
      if (customEvent.detail?.key !== key) {
        return
      }

      setValue(readStoredStateValue(customEvent.detail.raw, schema, options))
    }

    const handleStorage = (event: StorageEvent) => {
      if (event.key !== key) {
        return
      }

      setValue(readStoredStateValue(event.newValue, schema, options))
    }

    window.addEventListener(
      STORED_STATE_SYNC_EVENT,
      handleCustomSync as EventListener,
    )
    window.addEventListener("storage", handleStorage)
    return () => {
      window.removeEventListener(
        STORED_STATE_SYNC_EVENT,
        handleCustomSync as EventListener,
      )
      window.removeEventListener("storage", handleStorage)
    }
  }, [key, options, schema, storage])

  const setStoredValue = useCallback(
    (next: T | ((previous: T) => T)) => {
      setValue((previous) => {
        const resolvedValue =
          typeof next === "function" ? (next as (prev: T) => T)(previous) : next

        if (storage && typeof window !== "undefined") {
          window.dispatchEvent(
            new CustomEvent<StoredStateSyncDetail>(STORED_STATE_SYNC_EVENT, {
              detail: {
                key,
                raw: writeStoredStateValue(resolvedValue, options.version),
              },
            }),
          )
        }

        return resolvedValue
      })
    },
    [key, options.version, storage],
  )

  return [value, setStoredValue] as const
}
